import React, { useState, useEffect, useCallback, useMemo } from "react";
import { affiliateAPI } from "@/services/api";
import { fmtDateShort } from "@/utils/dateUtils";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import {
  LayoutDashboard, Link2, Megaphone, Wallet, Banknote, Package, User,
  TrendingUp, Award, Target, Zap, ShieldCheck, ArrowUpRight, Copy, Check,
  LogOut, Bell, Users, CircleDollarSign, Send, QrCode, Trophy, CreditCard, FileText as FileDoc,
} from "lucide-react";

/* ============ Brand: Fintech tepercaya — Stockara green ============ */
const BRAND = "#1A5733";        // hijau brand utama
const BRAND_700 = "#14502d";
const BRAND_50 = "#eaf3ee";     // hijau sangat muda (bg lembut)
const UP = "#16a34a";           // positif/naik
const INK = "#0f172a";          // teks utama
const MUTED = "#64748b";        // teks redup
const FAINT = "#94a3b8";
const LINE = "#e7ebe9";         // garis halus kehijauan
const BG = "#f5f8f6";           // latar
const CARD = "#ffffff";

const loc = () => window.location;
const nav = (p) => { window.history.pushState({}, "", p); window.dispatchEvent(new PopStateEvent("popstate")); };
// Cache-bust foto (nginx sempat 404-kan /api/uploads/affiliate/* & browser nyimpen 404 itu 7 hari)
const bust = (url) => url ? `${url}${url.includes("?") ? "&" : "?"}v=20260718` : url;

const TABS = [
  { id: "dashboard", label: "Dashboard", Icon: LayoutDashboard },
  { id: "referrals", label: "Referral", Icon: Link2 },
  { id: "campaigns", label: "Campaign", Icon: Megaphone },
  { id: "commissions", label: "Komisi", Icon: Wallet },
  { id: "payouts", label: "Pencairan", Icon: Banknote },
  { id: "content-kit", label: "Content Kit", Icon: Package },
  { id: "leaderboard", label: "Leaderboard", Icon: Trophy },
  { id: "kyc", label: "Verifikasi ID", Icon: ShieldCheck },
  { id: "profile", label: "Profil", Icon: User },
];
const BANKS = ["BCA","BNI","BRI","Mandiri","CIMB Niaga","Permata","Danamon","BSI","Jago","Jenius","OVO","GoPay","Dana","ShopeePay"];
const WALLETS = new Set(["OVO","GoPay","Dana","ShopeePay","LinkAja","ovo","gopay","dana","shopeepay","linkaja"]);
const STATUS_COLORS = { clicked:"#94a3b8", trial:"#0ea5e9", registered:"#0ea5e9", active:"#16a34a", converted:"#16a34a", churned:"#ef4444", pending:"#d97706", approved:"#16a34a", rejected:"#ef4444", paid:"#1A5733", requested:"#d97706", transferred:"#16a34a" };

const Badge = ({ s }) => <span style={{ background: (STATUS_COLORS[s] || "#94a3b8") + "1a", color: STATUS_COLORS[s] || "#64748b", padding: "3px 10px", borderRadius: 999, fontSize: 12, fontWeight: 700, textTransform: "capitalize", border: `1px solid ${(STATUS_COLORS[s] || "#94a3b8") + "33"}` }}>{s}</span>;
const Card = ({ children, style }) => <div style={{ background: CARD, borderRadius: 16, padding: 20, border: `1px solid ${LINE}`, boxShadow: "0 1px 2px rgba(16,24,40,.04)", ...style }}>{children}</div>;
const Stat = ({ label, value, sub, Icon }) => <Card style={{ padding: 18 }}>
  <div style={{ display: "flex", alignItems: "center", gap: 8, color: MUTED, fontSize: 12.5, fontWeight: 600 }}>{Icon && <Icon size={15} color={BRAND} />}{label}</div>
  <div style={{ fontSize: 26, fontWeight: 800, color: INK, margin: "6px 0 0", letterSpacing: "-0.02em" }}>{value}</div>
  {sub && <div style={{ fontSize: 12, color: FAINT, marginTop: 2 }}>{sub}</div>}
</Card>;
const Btn = ({ children, onClick, primary, ghost, disabled, small, style, type }) => <button type={type} onClick={onClick} disabled={disabled} style={{
  padding: small ? "8px 14px" : "11px 20px", borderRadius: 10, border: ghost ? `1px solid ${LINE}` : "none",
  background: disabled ? "#cbd5e1" : primary ? BRAND : ghost ? "#fff" : "#eef2f0",
  color: disabled ? "#fff" : primary ? "#fff" : ghost ? INK : BRAND,
  fontWeight: 700, cursor: disabled ? "default" : "pointer", fontSize: small ? 13 : 14,
  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7, transition: "filter .15s", ...style,
}} onMouseOver={e => { if (!disabled) e.currentTarget.style.filter = "brightness(0.95)"; }} onMouseOut={e => { e.currentTarget.style.filter = "none"; }}>{children}</button>;
const Input = ({ label, ...props }) => <div style={{ marginBottom: 14 }}>{label && <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 6 }}>{label}</label>}<input {...props} style={{ width: "100%", padding: "11px 13px", border: `1px solid ${LINE}`, borderRadius: 10, fontSize: 14, boxSizing: "border-box", outline: "none", ...props.style }} onFocus={e => e.target.style.borderColor = BRAND} onBlur={e => e.target.style.borderColor = LINE} /></div>;
const Select = ({ label, options, ...props }) => <div style={{ marginBottom: 14 }}>{label && <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 6 }}>{label}</label>}<select {...props} style={{ width: "100%", padding: "11px 13px", border: `1px solid ${LINE}`, borderRadius: 10, fontSize: 14, boxSizing: "border-box", background: "#fff" }}>{options.map(o => <option key={o} value={o}>{o || "Pilih..."}</option>)}</select></div>;
const Loader = () => <div style={{ textAlign: "center", padding: 48, color: FAINT, fontSize: 14 }}>Memuat...</div>;
const Empty = ({ t }) => <div style={{ textAlign: "center", padding: 40, color: FAINT, fontSize: 14 }}>{t || "Belum ada data"}</div>;
const Table = ({ cols, rows }) => rows.length === 0 ? <Empty /> : <div style={{ overflowX: "auto" }}><table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}><thead><tr>{cols.map(c => <th key={c.key} style={{ textAlign: "left", padding: "11px 10px", borderBottom: `1px solid ${LINE}`, color: MUTED, fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: ".03em" }}>{c.label}</th>)}</tr></thead><tbody>{rows.map((r, i) => <tr key={i} style={{ borderBottom: `1px solid ${BG}` }}>{cols.map(c => <td key={c.key} style={{ padding: "11px 10px", color: "#334155" }}>{c.render ? c.render(r) : r[c.key] ?? "-"}</td>)}</tr>)}</tbody></table></div>;

const fmt = (n) => typeof n === "number" ? `Rp ${n.toLocaleString("id-ID")}` : "Rp 0";
const fmtDate = (d) => fmtDateShort(d, "-");

export default function AffiliatePortal() {
  const [path, setPath] = useState(loc().pathname);
  useEffect(() => { const h = () => setPath(loc().pathname); window.addEventListener("popstate", h); return () => window.removeEventListener("popstate", h); }, []);

  const token = localStorage.getItem("stockara_affiliate_token");
  const subPath = path.replace("/affiliate", "").replace(/^\//, "") || "dashboard";

  // Landing personal per-affiliate: /affiliate/p/:code (akses publik)
  const pMatch = subPath.match(/^p\/(.+)$/);
  if (pMatch) return <Shell bare><PersonalLanding code={pMatch[1]} /></Shell>;

  if (!token && subPath !== "login" && subPath !== "register" && subPath !== "") return <Shell bare><Landing /></Shell>;
  if (subPath === "login") return <Shell bare><AuthForm mode="login" /></Shell>;
  if (subPath === "register") return <Shell bare><AuthForm mode="register" /></Shell>;
  if (!token) return <Shell bare><Landing /></Shell>;

  const page = TABS.find(t => t.id === subPath) ? subPath : "dashboard";
  return <Shell><AppShell page={page} /></Shell>;
}

function Shell({ children, bare }) {
  const logged = !!localStorage.getItem("stockara_affiliate_token");
  return <div style={{ minHeight: "100vh", background: BG, fontFamily: "Inter, system-ui, -apple-system, sans-serif" }}>
    <header style={{ background: "#fff", borderBottom: `1px solid ${LINE}`, padding: "0 24px", height: 60, display: "flex", justifyContent: "space-between", alignItems: "center", position: "sticky", top: 0, zIndex: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }} onClick={() => nav("/affiliate")}>
        <div style={{ width: 30, height: 30, borderRadius: 8, background: BRAND, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 16 }}>S</div>
        <div style={{ fontWeight: 800, fontSize: 17, color: INK, letterSpacing: "-0.01em" }}>Stockara <span style={{ color: BRAND }}>Affiliate</span></div>
      </div>
      {logged && <Btn small ghost onClick={() => { localStorage.removeItem("stockara_affiliate_token"); nav("/affiliate"); }}><LogOut size={15} /> Keluar</Btn>}
    </header>
    <div style={{ maxWidth: bare ? 1120 : 1180, margin: "0 auto", padding: "0 16px" }}>{children}</div>
  </div>;
}


/* ==================== PERSONAL LANDING (publik) =================== */
function PersonalLanding({ code }) {
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState("");
  React.useEffect(() => {
    affiliateAPI.publicProfile(code)
      .then(r => setData(r.data))
      .catch(() => setErr("Kode referral tidak ditemukan atau sudah tidak aktif."));
  }, [code]);

  const TIER_LABEL = { bronze: "Bronze", silver: "Silver", gold: "Gold" };

  if (err) return (
    <div style={{ minHeight: "60vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, textAlign: "center", padding: 32 }}>
      <div style={{ fontSize: 48 }}>&#128269;</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: INK }}>Link Tidak Ditemukan</div>
      <div style={{ fontSize: 14, color: MUTED, maxWidth: 360 }}>{err}</div>
      <Btn primary onClick={() => nav("/affiliate")}>Cek Stockara</Btn>
    </div>
  );
  if (!data) return <div style={{ textAlign: "center", padding: 80, color: FAINT }}>Memuat...</div>;

  const tier = data.tier || "bronze";
  const link = data.referral_link;

  return (
    <div style={{ maxWidth: 480, margin: "48px auto 80px", padding: "0 16px" }}>
      <div style={{ background: CARD, borderRadius: 20, border: `1px solid ${LINE}`, overflow: "hidden", marginBottom: 16 }}>
        <div style={{ background: `linear-gradient(135deg, ${BRAND} 0%, #14502d 100%)`, padding: "28px 24px 56px", textAlign: "center" }}>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,.7)", fontWeight: 600, letterSpacing: ".05em", marginBottom: 8 }}>DIAJAK BERGABUNG OLEH</div>
          {data.profile_photo_url
            ? <img src={bust(data.profile_photo_url)} alt={data.name} style={{ width: 80, height: 80, borderRadius: "50%", border: "3px solid rgba(255,255,255,.4)", objectFit: "cover", margin: "0 auto 12px", display: "block" }} />
            : <div style={{ width: 80, height: 80, borderRadius: "50%", background: "rgba(255,255,255,.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32, fontWeight: 800, color: "#fff", margin: "0 auto 12px" }}>{(data.name || "A")[0].toUpperCase()}</div>
          }
          <div style={{ fontWeight: 800, fontSize: 22, color: "#fff", letterSpacing: "-0.01em" }}>{data.name}</div>
          <div style={{ marginTop: 8, display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,.15)", borderRadius: 999, padding: "4px 14px" }}>
            <span style={{ fontSize: 13, color: "#fff", fontWeight: 600 }}>Affiliate {TIER_LABEL[tier] || tier}</span>
          </div>
          {data.bio && <div style={{ marginTop: 12, fontSize: 13, color: "rgba(255,255,255,.8)", lineHeight: 1.6, maxWidth: 340, margin: "12px auto 0" }}>{data.bio}</div>}
        </div>
        <div style={{ padding: "8px 24px 20px", marginTop: -32, position: "relative" }}>
          <div style={{ background: CARD, borderRadius: 14, border: `1px solid ${LINE}`, padding: "16px 20px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, boxShadow: "0 2px 8px rgba(0,0,0,.06)" }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 26, fontWeight: 800, color: BRAND }}>{data.active_referrals}</div>
              <div style={{ fontSize: 11, color: MUTED, fontWeight: 600, marginTop: 2 }}>TEMAN BERGABUNG</div>
            </div>
            <div style={{ textAlign: "center", borderLeft: `1px solid ${LINE}` }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#d97706" }}>Rp {(data.signup_bonus || 0).toLocaleString("id-ID")}</div>
              <div style={{ fontSize: 11, color: MUTED, fontWeight: 600, marginTop: 2 }}>BONUS BERGABUNG</div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ background: CARD, borderRadius: 20, border: `1px solid ${LINE}`, padding: "24px 24px 20px" }}>
        <div style={{ fontWeight: 800, fontSize: 20, color: INK, marginBottom: 6, lineHeight: 1.3 }}>Kelola stok dan kasir bisnismu lebih mudah</div>
        <div style={{ fontSize: 14, color: MUTED, marginBottom: 20, lineHeight: 1.7 }}>
          Bergabung via undangan <strong style={{ color: BRAND }}>{data.name}</strong> dan dapatkan <strong>akses penuh Stockara</strong> untuk bisnismu.
        </div>
        <a href={link} style={{ display: "block", background: BRAND, color: "#fff", textAlign: "center", padding: "14px 24px", borderRadius: 12, fontWeight: 800, fontSize: 16, textDecoration: "none" }}>
          Daftar Gratis Sekarang
        </a>
        <div style={{ textAlign: "center", marginTop: 12, fontSize: 12, color: FAINT }}>
          Kode referral: <strong style={{ color: BRAND }}>{data.referral_code}</strong>
        </div>
      </div>
    </div>
  );
}


/* ============================ LEADERBOARD ============================ */
function LeaderboardPage() {
  const [rows, setRows] = useState(null);
  const load = useCallback(async () => {
    try { const r = await affiliateAPI.leaderboard(); setRows(r.data); }
    catch { setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const TIER_C = { bronze: "#b45309", silver: "#64748b", gold: "#ca8a04" };
  const MEDAL = { 1: "🥇", 2: "🥈", 3: "🥉" };
  return <div>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
      <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: INK }}>Leaderboard Afiliasi</h2>
      <span style={{ fontSize: 13, color: MUTED }}>Top 20 berdasarkan total penghasilan</span>
    </div>
    {!rows ? <Loader /> : rows.length === 0 ? <Card><Empty t="Belum ada data leaderboard." /></Card> : (
      <Card style={{ padding: 0, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
          <thead>
            <tr style={{ background: BRAND_50 }}>
              <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700, color: BRAND, fontSize: 12 }}>#</th>
              <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700, color: BRAND, fontSize: 12 }}>NAMA</th>
              <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700, color: BRAND, fontSize: 12 }}>TIER</th>
              <th style={{ padding: "12px 16px", textAlign: "right", fontWeight: 700, color: BRAND, fontSize: 12 }}>TOTAL REFERRAL</th>
              <th style={{ padding: "12px 16px", textAlign: "right", fontWeight: 700, color: BRAND, fontSize: 12 }}>TOTAL PENGHASILAN</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.affiliate_id} style={{ borderBottom: `1px solid ${LINE}`, background: r.is_me ? BRAND_50 : "transparent", transition: "background .15s" }}
                onMouseOver={e => { if (!r.is_me) e.currentTarget.style.background = "#f8fafc"; }}
                onMouseOut={e => { if (!r.is_me) e.currentTarget.style.background = "transparent"; }}>
                <td style={{ padding: "13px 16px", fontWeight: 800, fontSize: 18, color: r.rank <= 3 ? "#ca8a04" : MUTED }}>{MEDAL[r.rank] || r.rank}</td>
                <td style={{ padding: "13px 16px" }}>
                  <div style={{ fontWeight: r.is_me ? 800 : 600, color: r.is_me ? BRAND : INK }}>{r.is_me ? "Kamu 🫵" : r.name}</div>
                </td>
                <td style={{ padding: "13px 16px" }}>
                  <span style={{ background: (TIER_C[r.tier]||"#94a3b8")+"22", color: TIER_C[r.tier]||"#64748b", padding: "3px 10px", borderRadius: 999, fontSize: 12, fontWeight: 700, textTransform: "uppercase" }}>{r.tier}</span>
                </td>
                <td style={{ padding: "13px 16px", textAlign: "right", color: MUTED }}>{r.total_referrals || 0}</td>
                <td style={{ padding: "13px 16px", textAlign: "right", fontWeight: 700, color: r.is_me ? BRAND : INK }}>{fmt(r.total_earning || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    )}
  </div>;
}

/* ============================ LANDING ============================ */
function Landing() {
  const VP = [
    [TrendingUp, "Komisi Bulanan", "Dapat komisi berulang setiap bulan selama customer-mu aktif berlangganan."],
    [Award, "Naik Tier, Naik Bayaran", "Bronze → Silver → Gold. Makin banyak referral, makin besar pengali komisinya."],
    [Target, "Lacak Performa", "Buat campaign terpisah & pantau klik, konversi, dan penghasilan real-time."],
    [Zap, "Pencairan Cepat", "Ajukan pencairan ke rekeningmu, proses transparan dengan bukti transfer."],
  ];
  const STEPS = [
    ["1", "Daftar gratis", "Buat akun afiliasi dalam 1 menit."],
    ["2", "Bagikan link", "Sebarkan link referral unikmu ke rekan bisnis."],
    ["3", "Dapat komisi", "Mereka berlangganan, kamu dibayar tiap bulan."],
  ];
  return <div style={{ paddingBottom: 64 }}>
    {/* Hero */}
    <div style={{ textAlign: "center", paddingTop: 64, paddingBottom: 16 }}>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 7, background: BRAND_50, color: BRAND, padding: "6px 14px", borderRadius: 999, fontSize: 13, fontWeight: 700, marginBottom: 22 }}>
        <ShieldCheck size={15} /> Program resmi & pembayaran transparan
      </div>
      <h1 style={{ fontSize: 44, lineHeight: 1.1, color: INK, margin: "0 auto 16px", maxWidth: 720, fontWeight: 800, letterSpacing: "-0.03em" }}>
        Hasilkan penghasilan tetap dari setiap <span style={{ color: BRAND }}>rekomendasi</span>.
      </h1>
      <p style={{ color: MUTED, fontSize: 18, maxWidth: 560, margin: "0 auto 30px", lineHeight: 1.6 }}>
        Rekomendasikan Stockara ke pemilik bisnis lain, dan dapatkan <strong style={{ color: INK }}>bonus pendaftaran + komisi bulanan berulang</strong>.
      </p>
      <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
        <Btn primary onClick={() => nav("/affiliate/register")} style={{ padding: "13px 28px", fontSize: 15 }}>Gabung Sekarang <ArrowUpRight size={18} /></Btn>
        <Btn ghost onClick={() => nav("/affiliate/login")} style={{ padding: "13px 28px", fontSize: 15 }}>Masuk</Btn>
      </div>
      <div style={{ display: "flex", gap: 22, justifyContent: "center", marginTop: 26, color: MUTED, fontSize: 13, flexWrap: "wrap" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Check size={15} color={UP} /> Gratis bergabung</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Check size={15} color={UP} /> Komisi berulang</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Check size={15} color={UP} /> Cair ke rekening</span>
      </div>
    </div>

    {/* Value props */}
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 16, marginTop: 48 }}>
      {VP.map(([Icon, t, d]) => <Card key={t} style={{ padding: 22 }}>
        <div style={{ width: 42, height: 42, borderRadius: 11, background: BRAND_50, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}><Icon size={21} color={BRAND} /></div>
        <div style={{ fontWeight: 700, fontSize: 16, color: INK, marginBottom: 6 }}>{t}</div>
        <div style={{ fontSize: 13.5, color: MUTED, lineHeight: 1.55 }}>{d}</div>
      </Card>)}
    </div>

    {/* How it works */}
    <div style={{ marginTop: 56 }}>
      <h2 style={{ textAlign: "center", fontSize: 26, fontWeight: 800, color: INK, marginBottom: 32, letterSpacing: "-0.02em" }}>Cara kerjanya</h2>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 16 }}>
        {STEPS.map(([n, t, d]) => <Card key={n} style={{ textAlign: "center", padding: 24 }}>
          <div style={{ width: 40, height: 40, borderRadius: 999, background: BRAND, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 17, margin: "0 auto 14px" }}>{n}</div>
          <div style={{ fontWeight: 700, fontSize: 16, color: INK, marginBottom: 6 }}>{t}</div>
          <div style={{ fontSize: 13.5, color: MUTED, lineHeight: 1.55 }}>{d}</div>
        </Card>)}
      </div>
    </div>

    {/* CTA banner */}
    <div style={{ marginTop: 48, background: BRAND, borderRadius: 20, padding: "40px 32px", textAlign: "center", color: "#fff" }}>
      <h2 style={{ fontSize: 26, fontWeight: 800, margin: "0 0 10px", letterSpacing: "-0.02em" }}>Siap mulai cari penghasilan?</h2>
      <p style={{ opacity: .9, fontSize: 15, margin: "0 0 22px" }}>Bergabung gratis hari ini. Tidak ada biaya, tidak ada syarat tersembunyi.</p>
      <Btn onClick={() => nav("/affiliate/register")} style={{ background: "#fff", color: BRAND, padding: "13px 30px", fontSize: 15 }}>Daftar Afiliasi <ArrowUpRight size={18} /></Btn>
    </div>
  </div>;
}

/* ============================ AUTH ============================ */
function AuthForm({ mode }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const [otp, setOtp] = useState("");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inviteCode = useMemo(() => new URLSearchParams(window.location.search).get("invite") || "", []);

  const submitRegister = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try {
      await affiliateAPI.registerRequestOtp({ ...form, invite_code: inviteCode });
      setOtpStep(true);
    } catch (ex) { setErr(ex?.response?.data?.detail || "Gagal mengirim kode verifikasi"); }
    setLoading(false);
  };

  const verifyOtp = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try {
      const res = await affiliateAPI.registerVerifyOtp({ email: form.email, otp });
      localStorage.setItem("stockara_affiliate_token", res.data.token);
      nav("/affiliate/dashboard");
    } catch (ex) { setErr(ex?.response?.data?.detail || "Kode verifikasi salah"); }
    setLoading(false);
  };

  const submitLogin = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try {
      const res = await affiliateAPI.login(form);
      localStorage.setItem("stockara_affiliate_token", res.data.token);
      nav("/affiliate/dashboard");
    } catch (ex) { setErr(ex?.response?.data?.detail || "Email atau password salah"); }
    setLoading(false);
  };

  if (mode === "register" && otpStep) return (
    <div style={{ maxWidth: 420, margin: "56px auto" }}>
      <Card style={{ padding: 28 }}>
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{ fontSize: 48, marginBottom: 10 }}>📧</div>
          <h2 style={{ margin: "0 0 6px", fontSize: 21, fontWeight: 800, color: INK }}>Cek Email Kamu</h2>
          <p style={{ color: MUTED, fontSize: 14, margin: 0 }}>
            Kode verifikasi 6 digit dikirim ke<br /><strong style={{ color: INK }}>{form.email}</strong>
          </p>
        </div>
        {err && <div style={{ background: "#fef2f2", color: "#dc2626", padding: 11, borderRadius: 10, marginBottom: 14, fontSize: 13 }}>{err}</div>}
        <form onSubmit={verifyOtp}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#475569", marginBottom: 6 }}>Kode Verifikasi (6 digit)</label>
            <input
              type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6}
              value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000" autoFocus
              style={{ width: "100%", padding: "14px 16px", border: "1.5px solid #e2e8f0", borderRadius: 10, fontSize: 26, fontWeight: 700, textAlign: "center", letterSpacing: 12, boxSizing: "border-box", outline: "none", fontFamily: "monospace" }} />
          </div>
          <Btn primary disabled={loading || otp.length !== 6} type="submit" style={{ width: "100%", marginTop: 4 }}>
            {loading ? "Memverifikasi..." : "Verifikasi & Masuk Dashboard"}
          </Btn>
        </form>
        <div style={{ textAlign: "center", marginTop: 16, fontSize: 13, color: MUTED }}>
          Tidak menerima kode?{" "}
          <button type="button" onClick={() => { setOtpStep(false); setOtp(""); setErr(""); }}
            style={{ background: "none", border: "none", color: BRAND, fontWeight: 700, cursor: "pointer", fontSize: 13, padding: 0 }}>
            Kirim ulang
          </button>
        </div>
      </Card>
    </div>
  );

  return <div style={{ maxWidth: 420, margin: "56px auto" }}>
    <Card style={{ padding: 28 }}>
      <h2 style={{ textAlign: "center", margin: "0 0 6px", fontSize: 22, fontWeight: 800, color: INK }}>
        {mode === "login" ? "Masuk ke akun afiliasi" : "Daftar jadi afiliasi"}
      </h2>
      <p style={{ textAlign: "center", color: MUTED, fontSize: 14, margin: "0 0 22px" }}>
        {mode === "login" ? "Lanjut pantau penghasilanmu." : "Mulai cari penghasilan dalam 1 menit."}
      </p>
      {mode === "register" && inviteCode && (
        <div style={{ background: BRAND_50, border: `1px solid ${BRAND}33`, borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 12.5, color: BRAND_700, fontWeight: 600, textAlign: "center" }}>
          🎉 Kamu diundang jadi Mitra Prioritas Stockara — komisi lebih tinggi dari jalur biasa.
        </div>
      )}
      {err && <div style={{ background: "#fef2f2", color: "#dc2626", padding: 11, borderRadius: 10, marginBottom: 14, fontSize: 13 }}>{err}</div>}
      <form onSubmit={mode === "login" ? submitLogin : submitRegister}>
        {mode === "register" && <Input label="Nama Lengkap" value={form.name} onChange={e => set("name", e.target.value)} required />}
        <Input label="Email" type="email" value={form.email} onChange={e => set("email", e.target.value)} required />
        <Input label="Password" type="password" value={form.password} onChange={e => set("password", e.target.value)} required />
        {mode === "register" && <Input label="No. WhatsApp (opsional)" value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="08xxxxxxxxxx" />}
        <Btn primary disabled={loading} type="submit" style={{ width: "100%", marginTop: 6 }}>
          {loading ? "Memproses..." : mode === "login" ? "Masuk" : "Daftar & Verifikasi Email →"}
        </Btn>
      </form>
      {mode === "register" && (
        <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 12, color: "#15803d" }}>
          ✓ Kode verifikasi akan dikirim ke email kamu untuk konfirmasi pendaftaran.
        </div>
      )}
      <div style={{ textAlign: "center", marginTop: 18, fontSize: 13.5, color: MUTED }}>
        {mode === "login"
          ? <span>Belum punya akun? <a href="/affiliate/register" onClick={e => { e.preventDefault(); nav("/affiliate/register"); }} style={{ color: BRAND, fontWeight: 700, textDecoration: "none" }}>Daftar gratis</a></span>
          : <span>Sudah punya akun? <a href="/affiliate/login" onClick={e => { e.preventDefault(); nav("/affiliate/login"); }} style={{ color: BRAND, fontWeight: 700, textDecoration: "none" }}>Masuk</a></span>}
      </div>
    </Card>
  </div>;
}

/* ============================ APP SHELL ============================ */
function AppShell({ page }) {
  return <div style={{ display: "flex", gap: 22, paddingTop: 22, paddingBottom: 48 }}>
    <nav style={{ width: 210, flexShrink: 0 }}>
      <div style={{ position: "sticky", top: 80, display: "flex", flexDirection: "column", gap: 3 }}>
        {TABS.map(t => { const active = page === t.id; const I = t.Icon; return <div key={t.id} onClick={() => nav(`/affiliate/${t.id}`)} style={{ padding: "11px 14px", borderRadius: 11, cursor: "pointer", background: active ? BRAND_50 : "transparent", color: active ? BRAND : "#475569", fontWeight: active ? 700 : 500, fontSize: 14, display: "flex", alignItems: "center", gap: 11 }}><I size={18} />{t.label}</div>; })}
      </div>
    </nav>
    <main style={{ flex: 1, minWidth: 0 }}>
      {page === "dashboard" && <DashboardPage />}
      {page === "referrals" && <ReferralsPage />}
      {page === "campaigns" && <CampaignsPage />}
      {page === "commissions" && <CommissionsPage />}
      {page === "payouts" && <PayoutsPage />}
      {page === "content-kit" && <ContentKitPage />}
      {page === "leaderboard" && <LeaderboardPage />}
      {page === "kyc" && <KYCPage />}
      {page === "profile" && <ProfilePage />}
    </main>
  </div>;
}

/* ============================ DASHBOARD ============================ */
function DashboardPage() {
  const [data, setData] = useState(null);
  const load = useCallback(async () => { try { const r = await affiliateAPI.dashboard(); setData(r.data); } catch { localStorage.removeItem("stockara_affiliate_token"); nav("/affiliate"); } }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loader />;
  const { balance, total_earning, active_referrals, funnel, komisi_bulan_ini, recent_referrals, notifications, tier, tier_bonus, next_tier, next_tier_need, affiliate } = data;
  const TIER_COLORS = { bronze: "#b45309", silver: "#64748b", gold: "#ca8a04" };
  return <div>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18, flexWrap: "wrap", gap: 10 }}>
      <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: INK }}>Dashboard</h2>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ background: (TIER_COLORS[tier] || "#64748b") + "1a", color: TIER_COLORS[tier] || "#64748b", padding: "5px 14px", borderRadius: 999, fontWeight: 800, fontSize: 12.5, textTransform: "uppercase", border: `1px solid ${(TIER_COLORS[tier] || "#64748b") + "40"}`, display: "inline-flex", alignItems: "center", gap: 6 }}><Award size={14} /> {tier} {tier_bonus > 1 ? `· ${tier_bonus}x` : ""}</span>
        {next_tier && <span style={{ fontSize: 12.5, color: MUTED }}>{next_tier_need} referral lagi ke {next_tier}</span>}
      </div>
    </div>

    {/* Hero earning card */}
    <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr 1fr", gap: 16, marginBottom: 16 }}>
      <div style={{ background: `linear-gradient(135deg, ${BRAND}, ${BRAND_700})`, borderRadius: 16, padding: 22, color: "#fff" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, opacity: .85, fontSize: 13, fontWeight: 600 }}><CircleDollarSign size={16} /> Saldo tersedia</div>
        <div style={{ fontSize: 34, fontWeight: 800, margin: "8px 0 4px", letterSpacing: "-0.02em" }}>{fmt(balance)}</div>
        <div style={{ fontSize: 12.5, opacity: .8 }}>Total penghasilan: {fmt(total_earning)}</div>
        <Btn onClick={() => nav("/affiliate/payouts")} style={{ background: "#fff", color: BRAND, marginTop: 16 }} small>Tarik Saldo <ArrowUpRight size={15} /></Btn>
      </div>
      <Stat label="Komisi Bulan Ini" value={fmt(komisi_bulan_ini)} Icon={TrendingUp} />
      <Stat label="Customer Aktif" value={active_referrals} sub="berlangganan & menghasilkan" Icon={Users} />
    </div>

    {/* Tier progress */}
    {next_tier && <Card style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 9, fontSize: 13.5 }}>
        <span style={{ fontWeight: 700, color: INK, display: "flex", alignItems: "center", gap: 7 }}><Award size={16} color={BRAND} /> Progress ke tier <span style={{ textTransform: "uppercase" }}>{next_tier}</span></span>
        <span style={{ color: MUTED }}><strong style={{ color: BRAND }}>{next_tier_need}</strong> referral lagi</span>
      </div>
      <div style={{ height: 10, background: BG, borderRadius: 999, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${Math.min(100, Math.round(active_referrals / Math.max(active_referrals + next_tier_need, 1) * 100))}%`, background: `linear-gradient(90deg, ${BRAND}, ${UP})`, borderRadius: 999, transition: "width .4s" }} />
      </div>
    </Card>}

    {/* Funnel */}
    <Card style={{ marginBottom: 16 }}>
      <h3 style={{ marginTop: 0, marginBottom: 14, fontSize: 15, fontWeight: 700, color: INK }}>Perjalanan Referral</h3>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {[["Klik", funnel.clicks, "#94a3b8"], ["Terdaftar", funnel.registered, "#0ea5e9"], ["Aktif", funnel.active, UP], ["Berhenti", funnel.churned, "#ef4444"]].map(([l, v, c]) => (
          <div key={l} style={{ flex: "1 1 120px", textAlign: "center", padding: "16px 0", background: c + "12", borderRadius: 12, border: `1px solid ${c}22` }}>
            <div style={{ fontSize: 26, fontWeight: 800, color: c }}>{v}</div>
            <div style={{ fontSize: 12.5, color: MUTED, marginTop: 2 }}>{l}</div>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 28, marginTop: 14, paddingTop: 12, borderTop: `1px solid ${BG}`, fontSize: 13, color: MUTED }}>
        <span>Conversion rate: <strong style={{ color: INK }}>{funnel.clicks ? Math.round((funnel.active / funnel.clicks) * 100) : 0}%</strong></span>
        <span>Penghasilan / klik (EPC): <strong style={{ color: INK }}>{funnel.clicks ? fmt(Math.round(total_earning / funnel.clicks)) : fmt(0)}</strong></span>
      </div>
    </Card>

    {/* Referral link */}
    <Card style={{ marginBottom: 16 }}>
      <h3 style={{ marginTop: 0, marginBottom: 12, fontSize: 15, fontWeight: 700, color: INK }}>Link Referral Kamu</h3>
      <CopyLink link={affiliate?.referral_link || ""} showShare />
    </Card>

    {/* Promo templates */}
    <Card style={{ marginBottom: 16 }}>
      <h3 style={{ marginTop: 0, marginBottom: 6, fontSize: 15, fontWeight: 700, color: INK }}>Teks Promosi Siap Pakai</h3>
      <p style={{ marginTop: 0, marginBottom: 8, fontSize: 12.5, color: MUTED }}>Salin & tempel ke WhatsApp/sosmed. Link referralmu sudah otomatis tertempel.</p>
      {PROMO_TEMPLATES.map((t, i) => <TemplateRow key={i} text={t} link={affiliate?.referral_link} />)}
    </Card>

    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
      <Card>
        <h3 style={{ marginTop: 0, marginBottom: 10, fontSize: 15, fontWeight: 700, color: INK }}>Referral Terbaru</h3>
        {recent_referrals.length === 0 ? <Empty t="Belum ada referral" /> : recent_referrals.map(r => <div key={r.referral_id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: `1px solid ${BG}`, fontSize: 13.5 }}>
          <span style={{ color: "#334155" }}>{r.tenant_name || r.tenant_email || "-"}</span><Badge s={r.status} />
        </div>)}
      </Card>
      <Card>
        <h3 style={{ marginTop: 0, marginBottom: 10, fontSize: 15, fontWeight: 700, color: INK, display: "flex", alignItems: "center", gap: 7 }}><Bell size={16} color={BRAND} /> Notifikasi</h3>
        {(!notifications || notifications.length === 0) ? <Empty t="Tidak ada notifikasi baru" /> : notifications.slice(0, 5).map(n => <div key={n.notification_id} style={{ padding: "9px 0", borderBottom: `1px solid ${BG}`, fontSize: 13.5, color: "#475569" }}>
          <div>{n.message}</div><div style={{ fontSize: 11.5, color: FAINT, marginTop: 2 }}>{fmtDate(n.created_at)}</div>
        </div>)}
        {notifications && notifications.length > 0 && <Btn small ghost style={{ marginTop: 10 }} onClick={async () => { await affiliateAPI.markNotificationsRead(); load(); }}>Tandai semua dibaca</Btn>}
      </Card>
    </div>
  </div>;
}

function CopyLink({ link, showShare }) {
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const copy = () => { navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  const waMsg = encodeURIComponent(`Halo! Mau kelola stok, kasir & laporan bisnismu lebih gampang? Coba Stockara, daftar lewat link saya ya: ${link}`);
  return <div>
    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
      <input readOnly value={link} style={{ flex: 1, padding: "11px 13px", border: `1px solid ${LINE}`, borderRadius: 10, fontSize: 13, background: BG, color: "#334155" }} />
      <Btn primary small onClick={copy} style={{ flexShrink: 0 }}>{copied ? <><Check size={15} /> Tersalin</> : <><Copy size={15} /> Salin</>}</Btn>
    </div>
    {showShare && link && <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
      <a href={`https://wa.me/?text=${waMsg}`} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
        <Btn small style={{ background: "#25D366", color: "#fff" }}><Send size={14} /> Bagikan ke WhatsApp</Btn>
      </a>
      <Btn small ghost onClick={() => setShowQr(v => !v)}><QrCode size={14} /> {showQr ? "Sembunyikan QR" : "Tampilkan QR"}</Btn>
    </div>}
    {showShare && showQr && link && <div style={{ marginTop: 12, textAlign: "center" }}>
      <img src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(link)}`} alt="QR referral" style={{ width: 180, height: 180, border: `1px solid ${LINE}`, borderRadius: 14, padding: 8, background: "#fff" }} />
      <div style={{ fontSize: 12, color: MUTED, marginTop: 6 }}>Scan untuk buka link referral</div>
    </div>}
  </div>;
}

const PROMO_TEMPLATES = [
  "Lagi cari aplikasi buat kelola stok, kasir, & laporan bisnis? Aku pakai Stockara, enak banget semua jadi satu. Daftar di sini ya: {link}",
  "Buat kamu yang punya toko/usaha, Stockara bantu pantau stok & omzet otomatis. Coba daftar lewat link ini: {link}",
  "Stop catat manual! Stockara bikin kelola bisnis jauh lebih rapi. Yuk daftar: {link}",
];
function TemplateRow({ text, link }) {
  const [copied, setCopied] = useState(false);
  const full = text.replace("{link}", link || "");
  const copy = () => { navigator.clipboard.writeText(full); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return <div style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "10px 0", borderBottom: `1px solid ${BG}` }}>
    <div style={{ flex: 1, fontSize: 13, color: "#334155", lineHeight: 1.5 }}>{full}</div>
    <Btn small ghost onClick={copy} style={{ flexShrink: 0 }}>{copied ? <Check size={14} /> : <Copy size={14} />}</Btn>
  </div>;
}

/* ============================ REFERRALS ============================ */
function ReferralsPage() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  useEffect(() => { affiliateAPI.referrals(filter || undefined).then(r => setData(r.data)).catch(() => {}); }, [filter]);
  return <div>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
      <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: INK }}>Referral</h2>
      <select value={filter} onChange={e => setFilter(e.target.value)} style={{ padding: "9px 13px", border: `1px solid ${LINE}`, borderRadius: 10, fontSize: 13, background: "#fff" }}>
        <option value="">Semua Status</option>
        {["trial", "active", "churned"].map(s => <option key={s} value={s}>{s}</option>)}
      </select>
    </div>
    {!data ? <Loader /> : <Card><Table cols={[
      { key: "tenant_name", label: "Customer" },
      { key: "tenant_email", label: "Email" },
      { key: "campaign_slug", label: "Campaign", render: r => r.campaign_slug || "-" },
      { key: "status", label: "Status", render: r => <Badge s={r.status} /> },
      { key: "registered_at", label: "Daftar", render: r => fmtDate(r.registered_at) },
      { key: "converted_at", label: "Konversi", render: r => fmtDate(r.converted_at) },
      { key: "subscription_plan", label: "Plan", render: r => r.subscription_plan || "-" },
    ]} rows={data} /></Card>}
  </div>;
}

/* ============================ CAMPAIGNS ============================ */
function CampaignsPage() {
  const [campaigns, setCampaigns] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", slug: "", description: "" });
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => { try { const r = await affiliateAPI.campaigns(); setCampaigns(r.data); } catch {} }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await affiliateAPI.createCampaign(form); setShowForm(false); setForm({ name: "", slug: "", description: "" }); load(); } catch {} finally { setLoading(false); }
  };
  const del = async (id) => { if (window.confirm("Hapus campaign ini?")) { await affiliateAPI.deleteCampaign(id); load(); } };

  return <div>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
      <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: INK }}>Campaign</h2>
      <Btn primary small onClick={() => setShowForm(v => !v)}>{showForm ? "Batal" : "+ Campaign Baru"}</Btn>
    </div>
    {showForm && <Card style={{ marginBottom: 16 }}>
      <form onSubmit={create}>
        <Input label="Nama Campaign" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
        <Input label="Slug (URL)" value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") }))} placeholder="contoh: promo-juni" />
        <Input label="Deskripsi" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
        <Btn primary disabled={loading} type="submit">{loading ? "Membuat..." : "Buat Campaign"}</Btn>
      </form>
    </Card>}
    {!campaigns ? <Loader /> : campaigns.length === 0 ? <Card><Empty t="Belum ada campaign. Buat campaign untuk melacak performa link referral." /></Card> : <div style={{ display: "grid", gap: 12 }}>
      {campaigns.map(c => <Card key={c.campaign_id}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: INK, display: "flex", alignItems: "center", gap: 8 }}>{c.name} <Badge s={c.status} /></div>
            <div style={{ fontSize: 12.5, color: MUTED, margin: "4px 0" }}>{c.description || "Tanpa deskripsi"}</div>
          </div>
          <Btn small onClick={() => del(c.campaign_id)} style={{ color: "#ef4444", background: "#fef2f2" }}>Hapus</Btn>
        </div>
        <div style={{ display: "flex", gap: 24, margin: "12px 0", fontSize: 13 }}>
          <span style={{ color: MUTED }}><strong style={{ color: INK }}>{c.stats?.clicks || 0}</strong> Klik</span>
          <span style={{ color: MUTED }}><strong style={{ color: INK }}>{c.stats?.leads || 0}</strong> Lead</span>
          <span style={{ color: MUTED }}><strong style={{ color: INK }}>{c.stats?.conversions || 0}</strong> Konversi</span>
        </div>
        <CopyLink link={c.referral_link} showShare />
      </Card>)}
    </div>}
  </div>;
}

/* ============================ COMMISSIONS ============================ */
function CommissionsPage() {
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    affiliateAPI.commissions().then(r => setData(r.data)).catch(() => {});
    affiliateAPI.commissionSummary().then(r => setSummary(r.data)).catch(() => {});
  }, []);
  const chartData = useMemo(() => (summary || []).slice().reverse().map(s => ({ name: s.period, total: s.total })), [summary]);
  return <div>
    <h2 style={{ marginBottom: 16, fontSize: 22, fontWeight: 800, color: INK }}>Komisi</h2>
    {summary && summary.length > 0 && <Card style={{ marginBottom: 16 }}>
      <h3 style={{ marginTop: 0, fontSize: 15, fontWeight: 700, color: INK }}>Tren Komisi (12 Bulan Terakhir)</h3>
      <ResponsiveContainer width="100%" height={210}>
        <BarChart data={chartData}><CartesianGrid strokeDasharray="3 3" stroke={LINE} /><XAxis dataKey="name" fontSize={11} stroke={FAINT} /><YAxis fontSize={11} stroke={FAINT} tickFormatter={v => `${(v/1000).toFixed(0)}k`} /><Tooltip formatter={v => fmt(v)} /><Bar dataKey="total" fill={BRAND} radius={[5,5,0,0]} /></BarChart>
      </ResponsiveContainer>
    </Card>}
    {summary && summary.length > 0 && <Card style={{ marginBottom: 16 }}>
      <h3 style={{ marginTop: 0, fontSize: 15, fontWeight: 700, color: INK }}>Ringkasan Bulanan</h3>
      <Table cols={[
        { key: "period", label: "Periode" },
        { key: "total", label: "Total", render: r => fmt(r.total) },
        { key: "count", label: "Transaksi" },
        { key: "approved", label: "Disetujui" },
        { key: "pending", label: "Pending" },
      ]} rows={summary} />
    </Card>}
    <Card>
      <h3 style={{ marginTop: 0, fontSize: 15, fontWeight: 700, color: INK }}>Detail Komisi</h3>
      {!data ? <Loader /> : <Table cols={[
        { key: "period_month", label: "Periode" },
        { key: "tenant_name", label: "Customer", render: r => r.tenant_name || r.tenant_id?.slice(0, 8) || "-" },
        { key: "subscription_plan", label: "Plan" },
        { key: "commission_amount", label: "Komisi", render: r => fmt(r.commission_amount) },
        { key: "signup_bonus", label: "Bonus", render: r => r.signup_bonus ? fmt(r.signup_bonus) : "-" },
        { key: "tier", label: "Tier", render: r => <Badge s={r.tier} /> },
        { key: "status", label: "Status", render: r => <Badge s={r.status} /> },
      ]} rows={data} />}
    </Card>
  </div>;
}

/* ============================ PAYOUTS ============================ */
function PayoutsPage() {
  const [payouts, setPayouts] = useState(null);
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const load = useCallback(async () => { try { const r = await affiliateAPI.payouts(); setPayouts(r.data); } catch {} }, []);
  useEffect(() => { load(); }, [load]);

  const request = async (e) => {
    e.preventDefault(); setLoading(true); setMsg("");
    try {
      await affiliateAPI.requestPayout({ amount: parseFloat(amount), notes });
      setMsg("Permintaan pencairan berhasil dikirim!"); setAmount(""); setNotes(""); load();
    } catch (ex) { setMsg(ex?.response?.data?.detail || "Gagal request"); } finally { setLoading(false); }
  };

  return <div>
    <h2 style={{ marginBottom: 16, fontSize: 22, fontWeight: 800, color: INK }}>Pencairan</h2>
    <Card style={{ marginBottom: 16 }}>
      <h3 style={{ marginTop: 0, fontSize: 15, fontWeight: 700, color: INK }}>Ajukan Pencairan</h3>
      {msg && <div style={{ background: msg.includes("berhasil") ? "#f0fdf4" : "#fef2f2", color: msg.includes("berhasil") ? "#16a34a" : "#dc2626", padding: 11, borderRadius: 10, marginBottom: 12, fontSize: 13 }}>{msg}</div>}
      <form onSubmit={request} style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
        <Input label="Jumlah (Rp)" type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="Min 50.000" required style={{ width: 200 }} />
        <Input label="Catatan" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Opsional" style={{ width: 200 }} />
        <Btn primary disabled={loading} type="submit" style={{ marginBottom: 14 }}>{loading ? "..." : "Ajukan Pencairan"}</Btn>
      </form>
      {amount && parseFloat(amount) >= 50000 && (
        <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10, padding: "10px 14px", fontSize: 13, color: "#166534", marginTop: 8, display: "flex", gap: 20, flexWrap: "wrap" }}>
          <span>Bruto: <strong>Rp {parseInt(amount).toLocaleString("id-ID")}</strong></span>
          <span>PPh 21 (~2.5%): <strong style={{color:"#dc2626"}}>- Rp {Math.round(parseFloat(amount)*0.025).toLocaleString("id-ID")}</strong></span>
          <span>Yang diterima: <strong>Rp {Math.round(parseFloat(amount)*0.975).toLocaleString("id-ID")}</strong></span>
        </div>
      )}
      <div style={{ fontSize: 12, color: FAINT, marginTop: 8 }}>Pencairan ditransfer ke rekening di menu Profil. Dipotong PPh 21 sesuai ketentuan. Hold period 14 hari sejak komisi dikreditkan.</div>
    </Card>
    <Card>
      <h3 style={{ marginTop: 0, fontSize: 15, fontWeight: 700, color: INK }}>Riwayat Pencairan</h3>
      {!payouts ? <Loader /> : <Table cols={[
        { key: "requested_at", label: "Tanggal", render: r => fmtDate(r.requested_at) },
        { key: "amount", label: "Bruto", render: r => fmt(r.amount) },
        { key: "tax_amount", label: "PPh", render: r => r.tax_amount ? <span style={{color:"#dc2626"}}>-{fmt(r.tax_amount)}</span> : "-" },
        { key: "net_amount", label: "Diterima", render: r => r.net_amount ? <strong style={{color:BRAND}}>{fmt(r.net_amount)}</strong> : fmt(r.amount) },
        { key: "bank_name", label: "Bank" },
        { key: "bank_account_number", label: "No. Rekening" },
        { key: "status", label: "Status", render: r => <Badge s={r.status} /> },
        { key: "transfer_proof_url", label: "Bukti", render: r => r.transfer_proof_url ? <a href={r.transfer_proof_url} target="_blank" rel="noreferrer" style={{ color: BRAND, fontWeight: 600 }}>Lihat</a> : "-" },
        { key: "notes", label: "Catatan", render: r => r.notes || "-" },
      ]} rows={payouts} />}
    </Card>
  </div>;
}

/* ============================ CONTENT KIT ============================ */
function ContentKitPage() {
  const [items, setItems] = useState(null);
  useEffect(() => { affiliateAPI.contentKit().then(r => setItems(r.data)).catch(() => setItems([])); }, []);
  return <div>
    <h2 style={{ marginBottom: 6, fontSize: 22, fontWeight: 800, color: INK }}>Content Kit</h2>
    <p style={{ color: MUTED, fontSize: 14, marginBottom: 20 }}>Materi promosi siap pakai untuk membantu kamu mempromosikan Stockara.</p>
    {!items ? <Loader /> : items.length === 0 ? <Card><Empty t="Belum ada content kit. Admin akan menambahkan materi promosi di sini." /></Card> : <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 16 }}>
      {items.map(item => <Card key={item.item_id} style={{ padding: 16 }}>
        {item.thumbnail_url && <img src={item.thumbnail_url} alt="" style={{ width: "100%", height: 150, objectFit: "cover", borderRadius: 10, marginBottom: 10 }} />}
        <div style={{ fontSize: 12, color: BRAND, fontWeight: 700, textTransform: "capitalize" }}>{item.category}</div>
        <div style={{ fontWeight: 700, fontSize: 16, color: INK, margin: "4px 0" }}>{item.title}</div>
        <div style={{ fontSize: 13, color: MUTED, marginBottom: 10 }}>{item.description}</div>
        {item.file_url && <a href={item.file_url} target="_blank" rel="noreferrer"><Btn small primary>Download</Btn></a>}
      </Card>)}
    </div>}
  </div>;
}

/* ============================ PROFILE ============================ */
function ProfilePage() {
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({});
  const [pwForm, setPwForm] = useState({ old_password: "", new_password: "" });
  const [msg, setMsg] = useState("");
  const [pwMsg, setPwMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => { try { const r = await affiliateAPI.me(); setProfile(r.data); setForm(r.data); } catch {} }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault(); setLoading(true); setMsg("");
    try { await affiliateAPI.updateMe(form); setMsg("Profil berhasil diupdate!"); load(); } catch (ex) { setMsg(ex?.response?.data?.detail || "Gagal update"); } finally { setLoading(false); }
  };
  const changePw = async (e) => {
    e.preventDefault(); setPwMsg("");
    try { await affiliateAPI.changePassword(pwForm); setPwMsg("Password berhasil diubah!"); setPwForm({ old_password: "", new_password: "" }); } catch (ex) { setPwMsg(ex?.response?.data?.detail || "Gagal"); }
  };

  const [photoLoading, setPhotoLoading] = useState(false);
  const [photoMsg, setPhotoMsg] = useState("");

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setPhotoMsg("File terlalu besar, maks. 5MB"); return; }
    setPhotoLoading(true); setPhotoMsg("");
    try {
      const fd = new FormData(); fd.append("photo", file);
      const r = await affiliateAPI.uploadProfilePhoto(fd);
      setProfile(p => ({ ...p, profile_photo_url: r.data.profile_photo_url }));
      setPhotoMsg("Foto profil berhasil diperbarui!");
    } catch (ex) { setPhotoMsg(ex?.response?.data?.detail || "Gagal upload foto"); }
    finally { setPhotoLoading(false); e.target.value = ""; }
  };

  if (!profile) return <Loader />;
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setSocial = (k, v) => setForm(f => ({ ...f, social_links: { ...f.social_links, [k]: v } }));
  const setNotifPref = (k, v) => setForm(f => ({ ...f, notification_preferences: { ...f.notification_preferences, [k]: v } }));

  return <div>
    <h2 style={{ marginBottom: 16, fontSize: 22, fontWeight: 800, color: INK }}>Profil</h2>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
      <Card>
        {/* Avatar / Foto Profil */}
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20, paddingBottom: 16, borderBottom: `1px solid ${LINE}` }}>
          <div style={{ position: "relative", flexShrink: 0 }}>
            {profile.profile_photo_url
              ? <img src={bust(profile.profile_photo_url)} alt="Foto profil" style={{ width: 80, height: 80, borderRadius: 40, objectFit: "cover", border: `3px solid ${BRAND}`, display: "block" }} />
              : <div style={{ width: 80, height: 80, borderRadius: 40, background: BRAND, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 30, fontWeight: 800 }}>{(profile.name || "?")[0].toUpperCase()}</div>
            }
            <label style={{ position: "absolute", bottom: 0, right: 0, background: "#fff", border: `2px solid ${LINE}`, borderRadius: "50%", width: 26, height: 26, display: "flex", alignItems: "center", justifyContent: "center", cursor: photoLoading ? "default" : "pointer", fontSize: 13, boxShadow: "0 1px 4px rgba(0,0,0,.12)" }} title="Ganti foto profil">
              <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handlePhotoUpload} style={{ display: "none" }} disabled={photoLoading} />
              {photoLoading ? "⏳" : "✏️"}
            </label>
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: INK }}>{profile.name || "-"}</div>
            <div style={{ fontSize: 13, color: MUTED }}>{profile.email}</div>
            {photoMsg && <div style={{ fontSize: 12, color: photoMsg.includes("berhasil") ? "#16a34a" : "#dc2626", marginTop: 4 }}>{photoMsg}</div>}
            <div style={{ fontSize: 12, color: FAINT, marginTop: 2 }}>Klik ✏️ untuk ganti foto (JPG/PNG, maks. 5MB)</div>
          </div>
        </div>
        <h3 style={{ marginTop: 0, fontSize: 16, fontWeight: 700, color: INK }}>Informasi Pribadi</h3>
        {msg && <div style={{ background: msg.includes("berhasil") ? "#f0fdf4" : "#fef2f2", color: msg.includes("berhasil") ? "#16a34a" : "#dc2626", padding: 11, borderRadius: 10, marginBottom: 12, fontSize: 13 }}>{msg}</div>}
        <form onSubmit={save}>
          <Input label="Nama" value={form.name || ""} onChange={e => set("name", e.target.value)} />
          <Input label="No. WhatsApp" value={form.phone || ""} onChange={e => set("phone", e.target.value)} />
          <Input label="Bio" value={form.bio || ""} onChange={e => set("bio", e.target.value)} placeholder="Ceritakan sedikit tentang kamu" />
          <h4 style={{ fontSize: 14, marginBottom: 8, marginTop: 16, color: INK }}>Identitas & Pajak</h4>
          <Input label="No. KTP / NIK (16 digit)" value={form.ktp_number || ""} onChange={e => set("ktp_number", e.target.value)} placeholder="1234567890123456" maxLength={16} />
          <Input label="NPWP (opsional, hemat 20% PPh)" value={form.npwp || ""} onChange={e => set("npwp", e.target.value)} placeholder="00.000.000.0-000.000" />
          <div style={{ fontSize: 11.5, color: FAINT, marginBottom: 12 }}>Komisi akan dipotong PPh 21: 2.5% (dengan NPWP) atau 3% (tanpa NPWP) sesuai ketentuan pajak Indonesia.</div>
          <h4 style={{ fontSize: 14, marginBottom: 8, marginTop: 4, color: INK }}>Social Media</h4>
          <Input label="Instagram" value={form.social_links?.instagram || ""} onChange={e => setSocial("instagram", e.target.value)} placeholder="@username" />
          <Input label="TikTok" value={form.social_links?.tiktok || ""} onChange={e => setSocial("tiktok", e.target.value)} placeholder="@username" />
          <Input label="YouTube" value={form.social_links?.youtube || ""} onChange={e => setSocial("youtube", e.target.value)} placeholder="URL channel" />
          <h4 style={{ fontSize: 14, marginBottom: 8, marginTop: 16, color: INK }}>Notifikasi</h4>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, marginBottom: 8 }}>
            <input type="checkbox" checked={form.notification_preferences?.whatsapp !== false} onChange={e => setNotifPref("whatsapp", e.target.checked)} /> WhatsApp
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
            <input type="checkbox" checked={form.notification_preferences?.email !== false} onChange={e => setNotifPref("email", e.target.checked)} /> Email
          </label>
          <Btn primary disabled={loading} type="submit" style={{ marginTop: 16 }}>{loading ? "..." : "Simpan"}</Btn>
        </form>
      </Card>
      <div>
        <BankChangeSection profile={profile} onDone={load} />
        <Card style={{ marginBottom: 16 }}>
          <h3 style={{ marginTop: 0, fontSize: 16, fontWeight: 700, color: INK }}>Ganti Password</h3>
          {pwMsg && <div style={{ background: pwMsg.includes("berhasil") ? "#f0fdf4" : "#fef2f2", color: pwMsg.includes("berhasil") ? "#16a34a" : "#dc2626", padding: 11, borderRadius: 10, marginBottom: 12, fontSize: 13 }}>{pwMsg}</div>}
          <form onSubmit={changePw}>
            <Input label="Password Lama" type="password" value={pwForm.old_password} onChange={e => setPwForm(f => ({ ...f, old_password: e.target.value }))} />
            <Input label="Password Baru" type="password" value={pwForm.new_password} onChange={e => setPwForm(f => ({ ...f, new_password: e.target.value }))} />
            <Btn primary type="submit">Ganti Password</Btn>
          </form>
        </Card>
        <Card style={{ background: BRAND_50, border: `1px solid ${BRAND}22` }}>
          <h3 style={{ marginTop: 0, fontSize: 16, fontWeight: 700, color: INK }}>Info Akun</h3>
          <div style={{ fontSize: 13.5, color: "#334155", display: "grid", gap: 6 }}>
            <div>Email: <strong>{profile.email}</strong></div>
            <div>Kode Referral: <strong style={{ color: BRAND }}>{profile.referral_code}</strong></div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>Status: <Badge s={profile.status} /></div>
            <div>Tier: <strong style={{ textTransform: "uppercase" }}>{profile.tier}</strong></div>
            <div>Bergabung: {fmtDate(profile.created_at)}</div>
          </div>
        </Card>
      </div>
    </div>
  </div>;
}


// ── KYC Verification Page ─────────────────────────────────────────────────
async function analyzePhoto(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const issues = [];
      const canvas = document.createElement('canvas');
      const W = Math.min(img.width, 300), H = Math.min(img.height, 300);
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, W, H);
      const data = ctx.getImageData(0, 0, W, H).data;
      let sum = 0, sumSq = 0, count = 0;
      for (let i = 0; i < data.length; i += 4) {
        const g = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
        sum += g; sumSq += g * g; count++;
      }
      const mean = sum / count;
      const variance = sumSq / count - mean * mean;
      if (file.size < 20 * 1024) issues.push("Ukuran file sangat kecil, kemungkinan kualitas rendah");
      if (file.size > 10 * 1024 * 1024) issues.push("Ukuran file melebihi 10MB, kompres atau pilih foto lain");
      if (img.width < 400 || img.height < 300) issues.push(`Resolusi terlalu rendah (${img.width}\xd7${img.height}px), gunakan kamera utama HP`);
      if (variance < 35) issues.push("Foto terlalu gelap atau buram, pastikan pencahayaan cukup dan kamera tidak goyang");
      resolve({ ok: issues.length === 0, issues, previewUrl: url, size: file.size, width: img.width, height: img.height });
    };
    img.onerror = () => resolve({ ok: false, issues: ["File tidak bisa dibaca sebagai gambar"], previewUrl: null });
    img.src = url;
  });
}

function PhotoSourceModal({ onCamera, onGallery, onClose }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 9999, display: "flex", alignItems: "flex-end", justifyContent: "center" }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: "20px 20px 0 0", padding: "24px 20px 36px", width: "100%", maxWidth: 500 }} onClick={e => e.stopPropagation()}>
        <div style={{ width: 40, height: 4, background: "#e2e8f0", borderRadius: 4, margin: "0 auto 20px" }} />
        <div style={{ fontSize: 16, fontWeight: 800, color: "#0f172a", marginBottom: 16, textAlign: "center" }}>Pilih Sumber Foto</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
          <button type="button" onClick={onCamera} style={{ padding: "20px 12px", border: "1.5px solid #e2e8f0", borderRadius: 14, background: "#f8fafc", cursor: "pointer", textAlign: "center" }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>📷</div>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>Ambil Foto</div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>Gunakan kamera HP</div>
          </button>
          <button type="button" onClick={onGallery} style={{ padding: "20px 12px", border: "1.5px solid #e2e8f0", borderRadius: 14, background: "#f8fafc", cursor: "pointer", textAlign: "center" }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>🖼️</div>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>Dari Galeri</div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>Pilih dari penyimpanan</div>
          </button>
        </div>
        <button type="button" onClick={onClose} style={{ width: "100%", padding: "12px", border: "none", borderRadius: 10, background: "#f1f5f9", color: "#475569", fontWeight: 600, cursor: "pointer", fontSize: 14 }}>Batal</button>
      </div>
    </div>
  );
}

function PhotoUploadBox({ label, emoji, value, analysis, onPickSource }) {
  const hasIssue = analysis && !analysis.ok;
  const hasOk = analysis && analysis.ok;
  return (
    <div>
      {analysis?.previewUrl ? (
        <div style={{ position: "relative", borderRadius: 12, overflow: "hidden", marginBottom: 10, border: "1px solid #e2e8f0" }}>
          <img src={analysis.previewUrl} alt={label} style={{ width: "100%", maxHeight: 200, objectFit: "cover", display: "block" }} />
          <div style={{ position: "absolute", top: 8, right: 8, background: hasOk ? "#16a34a" : "#dc2626", color: "#fff", borderRadius: 20, padding: "4px 12px", fontSize: 12, fontWeight: 700, boxShadow: "0 2px 6px rgba(0,0,0,0.2)" }}>
            {hasOk ? "✓ Foto OK" : "✕ Ada Masalah"}
          </div>
        </div>
      ) : (
        <div style={{ border: "2px dashed #cbd5e1", borderRadius: 12, padding: "32px 20px", textAlign: "center", background: "#f8fafc", marginBottom: 10 }}>
          <div style={{ fontSize: 44, marginBottom: 8 }}>{emoji}</div>
          <div style={{ fontSize: 13, color: "#94a3b8" }}>Belum ada foto dipilih</div>
        </div>
      )}
      {hasIssue && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", marginBottom: 10 }}>
          {analysis.issues.map((iss, i) => (
            <div key={i} style={{ fontSize: 12, color: "#dc2626", display: "flex", gap: 6, marginBottom: i < analysis.issues.length - 1 ? 5 : 0 }}>
              <span>✕</span><span>{iss}</span>
            </div>
          ))}
          <div style={{ fontSize: 11, color: "#b91c1c", marginTop: 6 }}>Kamu tetap bisa upload, tapi pastikan foto sekecil mungkin terhindar dari masalah di atas.</div>
        </div>
      )}
      {hasOk && (
        <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "8px 14px", marginBottom: 10, fontSize: 12, color: "#15803d", display: "flex", gap: 8, alignItems: "center" }}>
          <span>✓</span>
          <span>Foto terdeteksi normal &nbsp;·&nbsp; {(value.size / 1024).toFixed(0)} KB &nbsp;·&nbsp; {analysis.width}&times;{analysis.height}px</span>
        </div>
      )}
      <button type="button" onClick={onPickSource}
        style={{ width: "100%", padding: "11px 16px", background: value ? "#f1f5f9" : BRAND, color: value ? "#334155" : "#fff", border: `1.5px solid ${value ? "#e2e8f0" : BRAND}`, borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: "pointer", transition: "all .15s" }}>
        {value ? "🔄  Ganti Foto" : `📷  Pilih Foto ${label}`}
      </button>
    </div>
  );
}

function KYCPage() {
  const [kyc, setKyc] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [fullName, setFullName] = useState("");
  const [nik, setNik] = useState("");
  const [ktpFile, setKtpFile] = useState(null);
  const [ktpAnalysis, setKtpAnalysis] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [selfieAnalysis, setSelfieAnalysis] = useState(null);
  const [sourceModal, setSourceModal] = useState(null);
  const ktpCamRef = React.useRef();
  const ktpGalRef = React.useRef();
  const selfieCamRef = React.useRef();
  const selfieGalRef = React.useRef();

  const load = async () => {
    setPageLoading(true);
    try { const r = await affiliateAPI.kycStatus(); setKyc(r.data); } catch {}
    setPageLoading(false);
  };
  useEffect(() => { load(); }, []);

  const handleFile = async (file, type) => {
    if (!file) return;
    const analysis = await analyzePhoto(file);
    if (type === "ktp") { setKtpFile(file); setKtpAnalysis(analysis); }
    else { setSelfieFile(file); setSelfieAnalysis(analysis); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!fullName.trim()) { setMsg({ text: "Nama lengkap wajib diisi sesuai KTP", ok: false }); return; }
    if (!/^\d{16}$/.test(nik.trim())) { setMsg({ text: "NIK harus tepat 16 digit angka", ok: false }); return; }
    if (!ktpFile) { setMsg({ text: "Foto KTP belum dipilih", ok: false }); return; }
    if (!selfieFile) { setMsg({ text: "Foto selfie memegang KTP belum dipilih", ok: false }); return; }
    if (ktpFile.size > 10 * 1024 * 1024 || selfieFile.size > 10 * 1024 * 1024) { setMsg({ text: "Ukuran foto maksimal 10MB", ok: false }); return; }
    setSubmitLoading(true); setMsg(null);
    try {
      const fd = new FormData();
      fd.append("ktp", ktpFile);
      fd.append("selfie", selfieFile);
      fd.append("full_name", fullName.trim());
      fd.append("nik", nik.trim());
      const r = await affiliateAPI.kycUpload(fd);
      setMsg({ text: r.data.message || "Dokumen berhasil diupload!", ok: true });
      setKtpFile(null); setSelfieFile(null); setKtpAnalysis(null); setSelfieAnalysis(null);
      load();
    } catch (ex) {
      const status = ex?.response?.status;
      const detail = ex?.response?.data?.detail;
      let errMsg;
      if (status === 413) errMsg = "Ukuran foto terlalu besar. Kompres foto terlebih dahulu (maks. 10MB per foto).";
      else if (typeof detail === "string") errMsg = detail;
      else if (Array.isArray(detail)) errMsg = detail.map(d => d.msg || d).join(", ");
      else if (!ex?.response) errMsg = "Koneksi gagal. Pastikan internet stabil, lalu coba lagi.";
      else errMsg = "Upload gagal (error " + (status || "?") + "). Coba lagi.";
      setMsg({ text: errMsg, ok: false });
    } finally { setSubmitLoading(false); }
  };

  const st = kyc?.status || (pageLoading ? "loading" : "not_submitted");

  if (pageLoading) return <div style={{ textAlign: "center", padding: 80, color: FAINT, fontSize: 14 }}>Memuat...</div>;

  if (st === "approved") return (
    <div>
      <h2 style={{ marginBottom: 4, fontSize: 22, fontWeight: 800, color: INK }}>Verifikasi Identitas</h2>
      <p style={{ color: FAINT, fontSize: 14, marginBottom: 20 }}>Status verifikasi identitasmu.</p>
      <Card style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ width: 50, height: 50, background: "#16a34a", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, color: "#fff", flexShrink: 0 }}>✓</div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 17, color: "#15803d" }}>Identitas Terverifikasi</div>
            <div style={{ fontSize: 13, color: "#166534", marginTop: 3 }}>Dokumenmu sudah disetujui. Kamu bisa melakukan pencairan komisi.</div>
            {kyc.ktp_name_ocr && <div style={{ fontSize: 13, color: "#0f172a", marginTop: 8 }}>Nama di KTP: <strong>{kyc.ktp_name_ocr}</strong></div>}
          </div>
        </div>
      </Card>
      {(kyc.ktp_url || kyc.selfie_url) && (
        <Card>
          <div style={{ fontWeight: 700, fontSize: 14, color: INK, marginBottom: 12 }}>Dokumen Terverifikasi</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {kyc.ktp_url && <div><div style={{ fontSize: 11, color: FAINT, fontWeight: 600, marginBottom: 6 }}>FOTO KTP</div>
              <a href={bust(kyc.ktp_url)} target="_blank" rel="noreferrer"><img src={bust(kyc.ktp_url)} alt="KTP" style={{ width: "100%", borderRadius: 10, aspectRatio: "4/3", objectFit: "cover", border: "2px solid #bbf7d0" }} /></a></div>}
            {kyc.selfie_url && <div><div style={{ fontSize: 11, color: FAINT, fontWeight: 600, marginBottom: 6 }}>FOTO SELFIE</div>
              <a href={bust(kyc.selfie_url)} target="_blank" rel="noreferrer"><img src={bust(kyc.selfie_url)} alt="Selfie" style={{ width: "100%", borderRadius: 10, aspectRatio: "4/3", objectFit: "cover", border: "2px solid #bbf7d0" }} /></a></div>}
          </div>
        </Card>
      )}
    </div>
  );

  if (st === "pending_review") return (
    <div>
      <h2 style={{ marginBottom: 4, fontSize: 22, fontWeight: 800, color: INK }}>Verifikasi Identitas</h2>
      <p style={{ color: FAINT, fontSize: 14, marginBottom: 20 }}>Status verifikasi identitasmu.</p>
      <Card style={{ background: "#fffbeb", border: "1px solid #fde68a", marginBottom: 14 }}>
        <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
          <div style={{ fontSize: 36, lineHeight: 1 }}>⏳</div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 16, color: "#b45309" }}>Dokumen Sedang Direview</div>
            <div style={{ fontSize: 13, color: "#92400e", marginTop: 4, lineHeight: 1.6 }}>Tim kami sedang memverifikasi dokumenmu. Proses biasanya selesai dalam 1×24 jam. Notifikasi akan dikirim ke email setelah selesai.</div>
          </div>
        </div>
      </Card>
      {(kyc.ktp_url || kyc.selfie_url) && (
        <Card>
          <div style={{ fontWeight: 700, fontSize: 14, color: INK, marginBottom: 12 }}>Dokumen yang Dikirim</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {kyc.ktp_url && <div><div style={{ fontSize: 11, color: FAINT, fontWeight: 600, marginBottom: 6 }}>FOTO KTP</div>
              <a href={bust(kyc.ktp_url)} target="_blank" rel="noreferrer"><img src={bust(kyc.ktp_url)} alt="KTP" style={{ width: "100%", borderRadius: 10, aspectRatio: "4/3", objectFit: "cover", border: "1px solid #e2e8f0" }} /></a></div>}
            {kyc.selfie_url && <div><div style={{ fontSize: 11, color: FAINT, fontWeight: 600, marginBottom: 6 }}>FOTO SELFIE</div>
              <a href={bust(kyc.selfie_url)} target="_blank" rel="noreferrer"><img src={bust(kyc.selfie_url)} alt="Selfie" style={{ width: "100%", borderRadius: 10, aspectRatio: "4/3", objectFit: "cover", border: "1px solid #e2e8f0" }} /></a></div>}
          </div>
          {kyc.submitted_at && <div style={{ fontSize: 12, color: FAINT, marginTop: 10 }}>Dikirim: {fmtDate(kyc.submitted_at)}</div>}
        </Card>
      )}
    </div>
  );

  const isReupload = st === "rejected";
  const canSubmit = fullName.trim() && /^\d{16}$/.test(nik) && ktpFile && selfieFile && !submitLoading;

  return (
    <div>
      <h2 style={{ marginBottom: 4, fontSize: 22, fontWeight: 800, color: INK }}>
        {isReupload ? "Upload Ulang Dokumen" : "Verifikasi Identitas"}
      </h2>
      <p style={{ color: FAINT, fontSize: 14, marginBottom: 20 }}>
        {isReupload ? "Dokumenmu sebelumnya ditolak. Upload ulang sesuai catatan di bawah." : "Diperlukan sebelum kamu bisa melakukan pencairan komisi."}
      </p>

      {isReupload && kyc?.reject_reason && (
        <Card style={{ background: "#fef2f2", border: "1px solid #fecaca", marginBottom: 16 }}>
          <div style={{ display: "flex", gap: 10 }}>
            <span style={{ fontSize: 20 }}>❌</span>
            <div><div style={{ fontWeight: 700, color: "#dc2626", fontSize: 14, marginBottom: 4 }}>Alasan Penolakan</div>
            <div style={{ fontSize: 13, color: "#b91c1c", lineHeight: 1.6 }}>{kyc.reject_reason}</div></div>
          </div>
        </Card>
      )}

      <form onSubmit={submit}>
        {/* Card 1: Data diri */}
        <Card style={{ marginBottom: 14 }}>
          <div style={{ fontWeight: 800, fontSize: 15, color: INK, marginBottom: 16 }}>1 · Data Diri Sesuai KTP</div>
          <Input label="Nama Lengkap" value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Nama sesuai KTP, contoh: Budi Santoso" />
          <Input label="NIK (16 digit)" value={nik} onChange={e => setNik(e.target.value.replace(/\D/g, "").slice(0, 16))} placeholder="Contoh: 3171234567890001" maxLength={16} />
          {nik.length > 0 && nik.length < 16 && <div style={{ fontSize: 12, color: "#f59e0b", marginTop: -8, marginBottom: 12 }}>NIK {nik.length}/16 digit</div>}
        </Card>

        {/* Card 2: Foto KTP */}
        <Card style={{ marginBottom: 14 }}>
          <div style={{ fontWeight: 800, fontSize: 15, color: INK, marginBottom: 6 }}>2 · Foto KTP</div>
          <div style={{ fontSize: 13, color: "#475569", marginBottom: 14, lineHeight: 1.7 }}>
            Letakkan KTP di permukaan datar, foto dari depan. Pastikan <strong>seluruh KTP terlihat</strong>, tidak terpotong, tidak buram, dan tidak ada pantulan cahaya.
          </div>
          <PhotoUploadBox label="KTP" emoji="🪪" value={ktpFile} analysis={ktpAnalysis} onPickSource={() => setSourceModal("ktp")} />
        </Card>

        {/* Card 3: Selfie */}
        <Card style={{ marginBottom: 14 }}>
          <div style={{ fontWeight: 800, fontSize: 15, color: INK, marginBottom: 10 }}>3 · Selfie Memegang KTP</div>
          <div style={{ background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 10, padding: "12px 14px", marginBottom: 14 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: "#92400e", marginBottom: 8 }}>⚠️ Aturan Wajib</div>
            <div style={{ fontSize: 12, color: "#78350f", lineHeight: 1.9 }}>
              ✓ &nbsp;Wajah terlihat penuh dan jelas<br />
              ✓ &nbsp;KTP dipegang di depan dada menghadap kamera<br />
              ✗ &nbsp;Tidak boleh memakai <strong>masker</strong>, <strong>kacamata hitam</strong>, atau penutup wajah<br />
              ✗ &nbsp;Tidak boleh foto buram, terlalu gelap, atau terpotong
            </div>
          </div>
          <PhotoUploadBox label="Selfie" emoji="🤳" value={selfieFile} analysis={selfieAnalysis} onPickSource={() => setSourceModal("selfie")} />
        </Card>

        {msg && (
          <div style={{ padding: "12px 16px", borderRadius: 10, marginBottom: 14, fontSize: 13, fontWeight: 500, background: msg.ok ? "#f0fdf4" : "#fef2f2", color: msg.ok ? "#16a34a" : "#dc2626", border: `1px solid ${msg.ok ? "#bbf7d0" : "#fecaca"}` }}>
            {msg.ok ? "✓ " : "✕ "}{msg.text}
          </div>
        )}

        <Btn primary type="submit" disabled={!canSubmit}>
          {submitLoading ? "⏳  Mengupload & Membaca KTP..." : "Kirim untuk Verifikasi →"}
        </Btn>
        <div style={{ fontSize: 12, color: FAINT, marginTop: 10, lineHeight: 1.6 }}>
          Proses 5-30 detik karena sistem membaca nama dari KTP otomatis.<br />Jangan tutup atau refresh halaman saat loading.
        </div>
      </form>

      {sourceModal && (
        <PhotoSourceModal
          onCamera={() => { setSourceModal(null); if (sourceModal === "ktp") ktpCamRef.current?.click(); else selfieCamRef.current?.click(); }}
          onGallery={() => { setSourceModal(null); if (sourceModal === "ktp") ktpGalRef.current?.click(); else selfieGalRef.current?.click(); }}
          onClose={() => setSourceModal(null)}
        />
      )}
      <input ref={ktpCamRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={e => handleFile(e.target.files[0], "ktp")} />
      <input ref={ktpGalRef} type="file" accept="image/jpeg,image/png,image/jpg,image/webp" style={{ display: "none" }} onChange={e => handleFile(e.target.files[0], "ktp")} />
      <input ref={selfieCamRef} type="file" accept="image/*" capture="user" style={{ display: "none" }} onChange={e => handleFile(e.target.files[0], "selfie")} />
      <input ref={selfieGalRef} type="file" accept="image/jpeg,image/png,image/jpg,image/webp" style={{ display: "none" }} onChange={e => handleFile(e.target.files[0], "selfie")} />
    </div>
  );
}

// ── Bank Change with OTP ──────────────────────────────────────────────────
function BankChangeSection({ profile, onDone }) {
  const [step, setStep] = useState("view"); // view | form | otp
  const [form, setForm] = useState({
    bank_name: profile?.bank_name || "",
    bank_account_number: profile?.bank_account_number || "",
    bank_account_name: profile?.bank_account_name || "",
  });
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ text: "", ok: true });

  const isWallet = WALLETS.has(form.bank_name);
  const accLabel = isWallet ? "Nomor Telepon (terdaftar di " + form.bank_name + ")" : "Nomor Rekening";

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const requestOtp = async () => {
    if (!form.bank_name || !form.bank_account_number || !form.bank_account_name) {
      setMsg({ text: "Lengkapi semua data rekening sebelum meminta OTP", ok: false }); return;
    }
    setLoading(true); setMsg({ text: "", ok: true });
    try {
      const r = await affiliateAPI.requestBankOtp();
      setMsg({ text: r.data.message, ok: true });
      setStep("otp");
    } catch (ex) {
      setMsg({ text: ex?.response?.data?.detail || "Gagal mengirim OTP", ok: false });
    } finally { setLoading(false); }
  };

  const verify = async () => {
    if (!otp || otp.length !== 6) { setMsg({ text: "Masukkan kode OTP 6 digit", ok: false }); return; }
    setLoading(true); setMsg({ text: "", ok: true });
    try {
      await affiliateAPI.verifyBankOtp({ ...form, otp_code: otp });
      setMsg({ text: "Rekening berhasil diperbarui!", ok: true });
      setStep("view");
      setOtp("");
      if (onDone) onDone();
    } catch (ex) {
      setMsg({ text: ex?.response?.data?.detail || "Verifikasi OTP gagal", ok: false });
    } finally { setLoading(false); }
  };

  const hasBank = profile?.bank_name && profile?.bank_account_number;

  return (
    <Card style={{ marginBottom: 16 }}>
      <h3 style={{ marginTop: 0, fontSize: 16, fontWeight: 700, color: INK }}>Rekening Bank / E-Wallet</h3>

      {msg.text && (
        <div style={{ background: msg.ok ? "#f0fdf4" : "#fef2f2", color: msg.ok ? "#16a34a" : "#dc2626", padding: "10px 14px", borderRadius: 8, marginBottom: 12, fontSize: 13, border: `1px solid ${msg.ok ? "#bbf7d0" : "#fecaca"}` }}>
          {msg.text}
        </div>
      )}

      {/* VIEW mode */}
      {step === "view" && (
        <div>
          {hasBank ? (
            <div style={{ background: "#f8fafc", borderRadius: 10, padding: "12px 16px", marginBottom: 12, fontSize: 13 }}>
              <div style={{ fontWeight: 700, color: INK, marginBottom: 4 }}>{profile.bank_name}</div>
              <div style={{ color: "#475569" }}>{WALLETS.has(profile.bank_name) ? "No. Telepon" : "No. Rekening"}: <strong>{profile.bank_account_number}</strong></div>
              <div style={{ color: "#475569" }}>Atas Nama: <strong>{profile.bank_account_name}</strong></div>
            </div>
          ) : (
            <div style={{ color: FAINT, fontSize: 13, marginBottom: 12 }}>Belum ada rekening tersimpan. Tambahkan sekarang.</div>
          )}
          <div style={{ fontSize: 12, color: FAINT, marginBottom: 12 }}>
            🔒 Perubahan rekening memerlukan verifikasi via email OTP untuk keamanan.
          </div>
          <Btn primary small onClick={() => { setStep("form"); setMsg({ text: "", ok: true }); }}>
            {hasBank ? "Ubah Rekening" : "Tambah Rekening"}
          </Btn>
        </div>
      )}

      {/* FORM mode - input new bank details */}
      {step === "form" && (
        <div>
          <Select label="Bank / E-Wallet" options={["", ...BANKS]} value={form.bank_name} onChange={e => { set("bank_name", e.target.value); setMsg({ text: "", ok: true }); }} />
          <Input label={accLabel} value={form.bank_account_number} onChange={e => { set("bank_account_number", e.target.value); setMsg({ text: "", ok: true }); }} placeholder={isWallet ? "08xxxxxxxxxx" : "Nomor rekening"} />
          <Input label="Atas Nama (sesuai KTP)" value={form.bank_account_name} onChange={e => { set("bank_account_name", e.target.value); setMsg({ text: "", ok: true }); }} />
          <div style={{ fontSize: 12, color: FAINT, marginBottom: 12 }}>Kode OTP akan dikirim ke email kamu untuk konfirmasi perubahan.</div>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn onClick={() => { setStep("view"); setMsg({ text: "", ok: true }); }}>Batal</Btn>
            <Btn primary disabled={loading} onClick={requestOtp}>{loading ? "Mengirim OTP..." : "Kirim Kode OTP"}</Btn>
          </div>
        </div>
      )}

      {/* OTP mode */}
      {step === "otp" && (
        <div>
          <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#1d4ed8", marginBottom: 14 }}>
            📧 Kode OTP 6 digit sudah dikirim ke email kamu. Berlaku 10 menit.
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 4 }}>Kode OTP</label>
            <input
              type="text" maxLength={6} value={otp}
              onChange={e => { setOtp(e.target.value.replace(/\D/g, "")); setMsg({ text: "", ok: true }); }}
              placeholder="Masukkan 6 digit kode"
              style={{ width: "100%", padding: "10px 14px", border: "2px solid #6366f1", borderRadius: 8, fontSize: 18, fontWeight: 700, letterSpacing: 6, textAlign: "center", boxSizing: "border-box" }}
            />
          </div>
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <Btn onClick={() => { setStep("form"); setOtp(""); setMsg({ text: "", ok: true }); }}>Kembali</Btn>
            <Btn primary disabled={loading || otp.length !== 6} onClick={verify}>{loading ? "Memverifikasi..." : "Konfirmasi Perubahan"}</Btn>
          </div>
          <div style={{ fontSize: 12, color: FAINT }}>
            Tidak menerima kode? <button onClick={requestOtp} style={{ background: "none", border: "none", color: BRAND, cursor: "pointer", fontSize: 12, fontWeight: 600, padding: 0 }}>Kirim ulang</button>
          </div>
        </div>
      )}
    </Card>
  );
}

